import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader } from "lucide-react";

const DIVISIONS = [
  {
    key: "studio",
    name: "Relatable Studio",
    tagline: "Every story needs a place to begin.",
    body:
      "Our creative playground — a studio space built for content creation. It's where ideas get tested, scripts get refined, and cameras start rolling. A lot of Relatable's own content is born here. Many brand stories begin here too.",
    accent: "#FF5600", // Blaze Orange
    cta: "View Studio Work",
    state: "live",
    href: "/work",
  },
  {
    key: "marketing",
    name: "Relatable Marketing",
    tagline: "This is where everything comes together.",
    body:
      "Our end-to-end content and production vertical, built around one simple idea: good storytelling starts with understanding the problem first. From strategy and scripting to pre-production, filming, editing, design and post — every stage handled under one roof, by a team that understands how stories adapt across audiences and platforms.",
    accent: "#7100D0", // Royal Violet
    cta: "View Marketing Work",
    state: "live",
    href: "/work",
  },
  {
    key: "experience",
    name: "Relatable Experience",
    tagline: "Not everything meaningful happens on a screen.",
    body:
      "Where brands meet people in the real world. Immersive brand experiences — events, gatherings, pop-ups and moments where audiences don't just watch a brand, they feel it. The goal is simple: create spaces where stories unfold naturally and people walk away with something memorable.",
    accent: "#FF001D", // Racing Red
    cta: "Loading…",
    state: "loading",
    href: null,
  },
];

export default function Divisions() {
  return (
    <section className="bg-ink text-bone py-20 md:py-24 lg:py-28 border-t border-line">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
          <div>
            <p className="eyebrow text-fog mb-6">◐ Three Divisions · One Studio</p>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.92]">
              We don&rsquo;t try to look<br/>
              perfect. We try to look<br/>
              <em className="not-italic text-relatable">honest.</em>
            </h2>
          </div>
          <p className="max-w-md text-fog text-base md:text-lg leading-relaxed">
            What we really do is build relatable content — across three divisions that share one craft, one studio, and one ethic.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line">
          {DIVISIONS.map((d, i) => (
            <motion.div
              key={d.key}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className={`relative bg-ink p-10 md:p-14 group transition-colors ${d.state === "loading" ? "" : "hover:bg-charcoal cursor-pointer"}`}
              style={{ borderTop: `2px solid ${d.accent}` }}
            >
              <div className="flex items-start justify-between mb-10">
                <div className="flex items-center gap-3">
                  <span className="block w-2 h-2" style={{ backgroundColor: d.accent }} />
                  <span className="font-mono text-xs text-fog">0{i + 1}</span>
                </div>
                {d.state === "loading" ? (
                  <span className="eyebrow inline-flex items-center gap-2" style={{ color: d.accent }}>
                    <Loader className="w-3.5 h-3.5 animate-spin" /> Coming Soon
                  </span>
                ) : (
                  <ArrowUpRight className="w-4 h-4 text-fog group-hover:text-bone transition-colors" />
                )}
              </div>

              <h3
                className={`font-display text-3xl md:text-4xl lg:text-[40px] tracking-tight font-semibold leading-[1.02] ${d.state === "loading" ? "text-bone/50" : "text-bone"} transition-colors`}
                style={d.state === "loading" ? {} : undefined}
                onMouseEnter={(e) => { if (d.state !== "loading") e.currentTarget.style.color = d.accent; }}
                onMouseLeave={(e) => { if (d.state !== "loading") e.currentTarget.style.color = ""; }}
              >
                {d.name}
              </h3>

              <p className="mt-6 font-display italic text-bone/85 text-lg md:text-xl leading-snug max-w-md">
                &ldquo;{d.tagline}&rdquo;
              </p>

              <p className="mt-6 text-fog text-base leading-relaxed max-w-md">
                {d.body}
              </p>

              <div className="mt-12">
                {d.state === "loading" ? (
                  <span className="eyebrow text-fog inline-flex items-center gap-2">
                    {d.cta}
                    <span
                      className="block w-2 h-2 animate-flicker"
                      style={{ backgroundColor: d.accent }}
                    />
                  </span>
                ) : (
                  <Link to={d.href} className="eyebrow text-bone link-line">{d.cta} →</Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
